import React, { useState, useEffect } from 'react';
import { ShoppingBag, Menu, X, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getSessionUser, USER_SESSION_UPDATED_EVENT } from '../services/userService';
import { UserProfile } from '../types';
import { SHOP_PUBLIC_ENABLED } from '../utils/shopPublic';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    function syncUser() {
      setUser(getSessionUser());
    }
    syncUser();
    window.addEventListener(USER_SESSION_UPDATED_EVENT, syncUser);
    return () => window.removeEventListener(USER_SESSION_UPDATED_EVENT, syncUser);
  }, []);

  return (
    <header className="flex flex-wrap lg:justify-start lg:flex-nowrap z-50 w-full bg-mystic-box border-b border-mystic-box pb-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))]">
      <nav className="relative w-full flex flex-wrap lg:grid lg:grid-cols-12 basis-full items-center px-4 md:px-6 lg:px-8">
        <div className="lg:col-span-3 flex items-center">
          {/* Logo */}
          <a className="flex-none rounded-xl text-xl inline-block font-semibold focus:outline-none focus:opacity-80" href="/" aria-label="esoteric.pl">
             <img src="https://assets.esoteric.pl/img/logo-white.svg" alt="esoteric.pl" className="h-6 w-auto" width="162" height="24" />
          </a>
          {/* End Logo */}
          <div className="ms-1 sm:ms-2"></div>
        </div>

        {/* Button Group */}
        <div className="flex items-center gap-x-1 lg:gap-x-2 ms-auto py-1 lg:ps-6 lg:order-3 lg:col-span-3">
          {SHOP_PUBLIC_ENABLED && (
            <a
              href="/koszyk"
              className="size-[38px] relative flex justify-center items-center rounded-xl bg-mystic-box text-white transition-colors hover:text-mystic-accent focus:outline-none focus:text-mystic-accent"
            >
              <span className="sr-only">Cart</span>
              <ShoppingBag className="shrink-0 size-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-normal w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </a>
          )}

          {user?.avatar ? (
            <a
              href="/portal"
              className="relative size-[34px] shrink-0 overflow-hidden rounded-full bg-mystic-box transition-opacity duration-200 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-mystic-accent focus-visible:ring-offset-2 focus-visible:ring-offset-mystic-box"
              aria-label="Portal użytkownika"
            >
              <img
                src={user.avatar}
                alt=""
                width={34}
                height={34}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            </a>
          ) : (
            <a
              href="/portal"
              className="size-[38px] flex justify-center items-center rounded-xl bg-mystic-box text-white transition-colors hover:text-mystic-accent focus:outline-none focus:text-mystic-accent"
              aria-label={user ? 'Portal użytkownika' : 'Zaloguj się'}
            >
              <User className="shrink-0 size-5" />
            </a>
          )}

          <div className="lg:hidden">
            <button
              type="button"
              className="size-[38px] flex justify-center items-center rounded-xl bg-mystic-box text-white transition-colors hover:text-mystic-accent focus:outline-none focus:text-mystic-accent"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation"
            >
              {isMobileMenuOpen ? <X className="shrink-0 size-5" /> : <Menu className="shrink-0 size-5" />}
            </button>
          </div>
        </div>
        {/* End Button Group */}

        {/* Collapse */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:block overflow-hidden transition-all duration-300 basis-full grow lg:w-auto lg:basis-auto lg:order-2 lg:col-span-6`}>
          <div className="flex flex-col gap-y-4 gap-x-0 mt-5 lg:flex-row lg:justify-center lg:items-center lg:gap-y-0 lg:gap-x-7 lg:mt-0 font-medium">
            <div>
              <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/" aria-current="page">Start</a>
            </div>
            <div>
              <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/horoskop">Horoskop</a>
            </div>
            <div>
              <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/faza-ksiezyca">
                Faza Księżyca
              </a>
            </div>
            <div>
              <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/eksperci">Eksperci</a>
            </div>
            {SHOP_PUBLIC_ENABLED && (
              <div>
                <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/sklep">Sklep</a>
              </div>
            )}
            <div>
              <a className="inline-block text-gray-300 transition-colors hover:text-mystic-accent focus:text-mystic-accent focus:outline-none" href="/blog">Wiedza</a>
            </div>
          </div>
        </div>
        {/* End Collapse */}
      </nav>
    </header>
  );
};

export default Header;